import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGoBack } from '../../hooks/useReturnNavigation';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useCreateOKR, useUpdateOKR, useOKR } from '../../hooks';
import { useSubmitForApproval } from '../../hooks/useApprovals';
import { useUsers } from '../../hooks/useUsers';
import { useUserDepartments } from '../../hooks/useUserDepartments';
import { useTeams, useTeamMembers } from '../../hooks/useTeams';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ParentOKRSelector } from '../../components/okr/ParentOKRSelector';
import { cycleService, OKRCycle } from '../../services/cycle.service';
import { getErrorMessage } from '../../services/api';

interface KeyResultForm {
  title: string;
  description: string;
  unitType: 'percentage' | 'number' | 'currency' | 'boolean';
  startValue: number;
  targetValue: number;
  unit: string;
  ownerId?: string;
}

export const CreateOKR: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isEditMode = !!id;
  // Back, Cancel and Save all return to the view that opened this form (utils/returnTo.ts).
  const goBack = useGoBack(isEditMode ? `/okrs/${id}` : '/okrs');

  const createOKRMutation = useCreateOKR();
  const updateOKRMutation = useUpdateOKR();
  const submitForApprovalMutation = useSubmitForApproval();
  const { data: existingOKR, isLoading: isLoadingOKR } = useOKR(id || '');

  // Only load users if user is manager or account_admin
  const canAssignToOthers = user?.role === 'manager' || user?.role === 'account_admin';
  const { data: usersData } = useUsers({ limit: 100 }, canAssignToOthers);

  // Determine if user can change OKR type (employees can only create individual OKRs)
  const isEmployee = user?.role === 'employee';
  const canChangeOKRType = !isEmployee;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'individual' as 'company' | 'department' | 'team' | 'individual',
    ownerId: user?.id || '',
    departmentId: '' as string, // Will be populated from owner's departments
    teamId: '' as string, // For team-type OKRs
    parentOKRId: null as string | null,
    cycleId: '' as string,
    startDate: '' as string,
    endDate: '' as string,
    status: 'draft' as 'draft' | 'active' | 'on_track' | 'at_risk' | 'off_track' | 'completed'
  });

  // Fetch the selected owner's department memberships
  const { data: ownerDepartments, isLoading: loadingOwnerDepartments } = useUserDepartments(formData.ownerId);

  // Fetch teams and team members for team-type OKRs
  const { data: allTeams = [] } = useTeams();
  const { data: teamMembers = [] } = useTeamMembers(formData.type === 'team' ? formData.teamId : '');

  // Track the previous owner to detect owner changes
  const [previousOwnerId, setPreviousOwnerId] = useState<string | null>(null);

  // Auto-select primary department ONLY when in CREATE mode:
  // 1. Creating a new OKR and no department is set yet
  // 2. Creating a new OKR and user explicitly changes owner
  // NEVER auto-select in edit mode - preserve the saved departmentId
  useEffect(() => {
    // In edit mode, never auto-select - preserve the saved departmentId
    if (isEditMode) {
      setPreviousOwnerId(formData.ownerId);
      return;
    }

    if (!ownerDepartments || ownerDepartments.length === 0) {
      // Clear department if owner has no departments
      if (formData.departmentId) {
        setFormData(prev => ({ ...prev, departmentId: '' }));
      }
      setPreviousOwnerId(formData.ownerId);
      return;
    }

    const ownerChanged = previousOwnerId !== null && previousOwnerId !== formData.ownerId;
    const isNewOKRWithNoDepartment = !formData.departmentId;

    // Only auto-select primary if owner changed OR creating new OKR with no dept selected
    if (ownerChanged || isNewOKRWithNoDepartment) {
      const primaryDept = ownerDepartments.find(d => d.isPrimary) || ownerDepartments[0];
      if (primaryDept) {
        setFormData(prev => ({ ...prev, departmentId: primaryDept.departmentId }));
      }
    }

    setPreviousOwnerId(formData.ownerId);
  }, [ownerDepartments, formData.ownerId, isEditMode]);

  // Cycles state
  const [cycles, setCycles] = useState<OKRCycle[]>([]);
  const [loadingCycles, setLoadingCycles] = useState(true);
  const [datesFromCycle, setDatesFromCycle] = useState(false); // Track if dates were auto-filled

  const [keyResults, setKeyResults] = useState<KeyResultForm[]>([
    {
      title: '',
      description: '',
      unitType: 'percentage',
      startValue: 0,
      targetValue: 100,
      unit: '%'
    }
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch cycles on mount
  useEffect(() => {
    const fetchCycles = async () => {
      try {
        setLoadingCycles(true);
        const allCycles = await cycleService.getCycles();
        // Filter to only active and upcoming cycles
        const availableCycles = allCycles.filter(c => c.status === 'active' || c.status === 'upcoming');
        setCycles(availableCycles);

        // For new OKRs, default to active cycle
        if (!isEditMode) {
          const activeCycle = availableCycles.find(c => c.status === 'active');
          if (activeCycle) {
            setFormData(prev => ({
              ...prev,
              cycleId: activeCycle.id,
              startDate: activeCycle.startDate.split('T')[0],
              endDate: activeCycle.endDate.split('T')[0]
            }));
            setDatesFromCycle(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch cycles:', error);
      } finally {
        setLoadingCycles(false);
      }
    };

    fetchCycles();
  }, [isEditMode]);

  // Load existing OKR data when in edit mode
  useEffect(() => {
    if (isEditMode && existingOKR) {
      setFormData({
        title: existingOKR.title,
        description: existingOKR.description || '',
        type: existingOKR.type as 'company' | 'department' | 'team' | 'individual',
        ownerId: existingOKR.ownerId,
        departmentId: existingOKR.departmentId || '', // Load existing department
        teamId: (existingOKR as any).teamId || '',
        parentOKRId: existingOKR.parentOKRId || null,
        cycleId: existingOKR.cycleId || '',
        startDate: existingOKR.startDate ? existingOKR.startDate.split('T')[0] : '',
        endDate: existingOKR.endDate ? existingOKR.endDate.split('T')[0] : '',
        status: existingOKR.status as 'draft' | 'active' | 'on_track' | 'at_risk' | 'off_track' | 'completed'
      });

      // Load existing key results
      if (existingOKR.keyResults && existingOKR.keyResults.length > 0) {
        setKeyResults(
          existingOKR.keyResults.map(kr => ({
            title: kr.title,
            description: kr.description || '',
            unitType: kr.unitType as 'percentage' | 'number' | 'currency' | 'boolean',
            startValue: kr.startValue,
            targetValue: kr.targetValue,
            unit: kr.unit || '%',
            ownerId: kr.ownerId || ''
          }))
        );
      }
    }
  }, [isEditMode, existingOKR]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // If dates are manually changed, mark as no longer from cycle
    if (name === 'startDate' || name === 'endDate') {
      setDatesFromCycle(false);
    }
  };

  // Handle cycle selection - auto-populate dates
  const handleCycleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cycleId = e.target.value;
    const selectedCycle = cycles.find(c => c.id === cycleId);

    if (selectedCycle) {
      setFormData(prev => ({
        ...prev,
        cycleId,
        startDate: selectedCycle.startDate.split('T')[0],
        endDate: selectedCycle.endDate.split('T')[0]
      }));
      setDatesFromCycle(true);
    } else {
      // No cycle selected - clear cycle but keep dates
      setFormData(prev => ({
        ...prev,
        cycleId: ''
      }));
      setDatesFromCycle(false);
    }
  };

  const handleKeyResultChange = (index: number, field: string, value: string | number) => {
    const updated = [...keyResults];
    updated[index] = { ...updated[index], [field]: value };
    setKeyResults(updated);
  };

  const addKeyResult = () => {
    setKeyResults([
      ...keyResults,
      {
        title: '',
        description: '',
        unitType: 'percentage',
        startValue: 0,
        targetValue: 100,
        unit: '%'
      }
    ]);
  };

  const removeKeyResult = (index: number) => {
    setKeyResults(keyResults.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent, shouldSubmitForApproval: boolean = false) => {
    e.preventDefault();

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.ownerId) newErrors.ownerId = 'Owner is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      let okrId: string;

      if (isEditMode && id) {
        // Update existing OKR (without keyResults - those are handled separately)
        await updateOKRMutation.mutateAsync({
          id,
          data: {
            title: formData.title,
            description: formData.description,
            type: formData.type,
            ownerId: formData.ownerId,
            departmentId: formData.departmentId || undefined,
            status: formData.status,
            parentOKRId: formData.parentOKRId || undefined,
            cycleId: formData.cycleId || null,
            startDate: formData.startDate || undefined,
            endDate: formData.endDate || undefined
          }
        });
        okrId = id;
        toast.success('OKR updated successfully');
      } else {
        // Create new OKR with key results
        const newOKR = await createOKRMutation.mutateAsync({
          title: formData.title,
          description: formData.description,
          type: formData.type,
          ownerId: formData.ownerId,
          departmentId: formData.departmentId || undefined,
          parentOKRId: formData.parentOKRId || undefined,
          cycleId: formData.cycleId || undefined,
          startDate: formData.startDate || undefined,
          endDate: formData.endDate || undefined,
          teamId: formData.type === 'team' && formData.teamId ? formData.teamId : undefined,
          keyResults: keyResults
            .filter(kr => kr.title.trim())
            .map(kr => ({
              ...kr,
              startValue: Number(kr.startValue),
              targetValue: Number(kr.targetValue),
              ownerId: kr.ownerId || undefined
            }))
        });
        okrId = newOKR.id;
        toast.success('OKR created successfully');
      }

      // If user chose "Create and Submit", submit for approval
      if (shouldSubmitForApproval && !isEditMode) {
        await submitForApprovalMutation.mutateAsync(okrId);
        toast.success('OKR submitted for approval');
      }

      // Saved: return to where the user came from (the OKR, or a filtered list).
      goBack();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setErrors({ submit: errorMessage || `Failed to ${isEditMode ? 'update' : 'create'} OKR` });
    }
  };

  const handleCreateAndSubmit = (e: React.FormEvent) => {
    handleSubmit(e, true);
  };

  // Build user options based on role
  const userOptions = canAssignToOthers
    ? (usersData?.users.map(u => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName}`,
        disabled: false
      })) || [])
    : user
    ? [{
        value: user.id,
        label: `${user.firstName} ${user.lastName}`,
        disabled: false
      }]
    : [];

  // Show loading state while fetching existing OKR
  if (isEditMode && isLoadingOKR) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const mutation = isEditMode ? updateOKRMutation : createOKRMutation;
  const isLoading = mutation.isPending || submitForApprovalMutation.isPending;

  return (
    <div className="min-h-screen bg-cream">
      {/* Header - Warm gradient */}
      <div className="bg-gradient-to-r from-stone-800 via-stone-700 to-primary-600">
        <div className="px-6 lg:px-8 py-6">
          <div className="flex items-center">
            <Button variant="ghost" onClick={goBack} className="mr-4 text-white hover:bg-white/10">
              ← Back
            </Button>
            <div>
              <h1 className="text-3xl font-semibold text-white" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
                {isEditMode ? 'Edit OKR' : 'Create New OKR'}
              </h1>
              <p className="text-sm text-stone-300 mt-1">
                {isEditMode ? 'Update your objective and key results' : 'Define your objective and measurable key results'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          {/* Two column layout on large screens */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Main form - 2 columns */}
            <Card className="p-6 lg:col-span-2">
              <h2 className="text-xl font-semibold text-stone-900 mb-6" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Objective Information</h2>

              <div className="space-y-5">
                <Input
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  error={errors.title}
                  placeholder="e.g., Improve customer satisfaction"
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                    placeholder="What do you want to achieve? Be specific about the outcome."
                  />
                </div>

                {/* Three column grid for type, owner, and department */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Select
                      label="Type"
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      options={[
                        { value: 'company', label: 'Company', disabled: false },
                        { value: 'department', label: 'Department', disabled: false },
                        { value: 'team', label: 'Team', disabled: false },
                        { value: 'individual', label: 'Individual', disabled: false }
                      ]}
                      disabled={!canChangeOKRType}
                    />
                    {isEmployee && (
                      <p className="text-xs text-stone-500 mt-1">
                        Employees can only create Individual OKRs
                      </p>
                    )}
                  </div>

                  <Select
                    label="Owner"
                    name="ownerId"
                    value={formData.ownerId}
                    onChange={handleChange}
                    options={userOptions}
                    error={errors.ownerId}
                    disabled={!canAssignToOthers}
                  />

                  <Select
                    label="Department"
                    name="departmentId"
                    value={formData.departmentId}
                    onChange={handleChange}
                    options={
                      ownerDepartments && ownerDepartments.length > 0
                        ? ownerDepartments.map(d => ({
                            value: d.departmentId,
                            label: d.departmentName + (d.isPrimary ? ' (Primary)' : ''),
                            disabled: false
                          }))
                        : [{ value: '', label: 'No departments available', disabled: true }]
                    }
                    disabled={loadingOwnerDepartments || !ownerDepartments || ownerDepartments.length === 0}
                  />
                </div>

                {/* Team selector - shown only for team-type OKRs */}
                {formData.type === 'team' && (
                  <Select
                    label="Team"
                    name="teamId"
                    value={formData.teamId}
                    onChange={handleChange}
                    options={[
                      { value: '', label: 'Select a team', disabled: false },
                      ...allTeams.map(t => ({
                        value: t.id,
                        label: t.name,
                        disabled: false
                      }))
                    ]}
                  />
                )}

                <ParentOKRSelector
                  okrType={formData.type}
                  value={formData.parentOKRId}
                  onChange={(parentId) => setFormData(prev => ({ ...prev, parentOKRId: parentId }))}
                  disabled={isLoading}
                  excludeId={isEditMode ? id : undefined}
                />

                {isEditMode && existingOKR && (existingOKR.status === 'active' || user?.role === 'manager' || user?.role === 'account_admin') && (
                  <Select
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    options={
                    user?.role === 'manager' || user?.role === 'account_admin'
                      ? [
                          { value: 'draft', label: 'Draft', disabled: false },
                          { value: 'active', label: 'Active', disabled: false },
                          { value: 'on_track', label: 'On Track', disabled: false },
                          { value: 'at_risk', label: 'At Risk', disabled: false },
                          { value: 'off_track', label: 'Off Track', disabled: false },
                          { value: 'completed', label: 'Completed', disabled: false }
                        ]
                      : [
                          { value: 'active', label: 'Active', disabled: false },
                          { value: 'on_track', label: 'On Track', disabled: false },
                          { value: 'at_risk', label: 'At Risk', disabled: false },
                          { value: 'off_track', label: 'Off Track', disabled: false },
                          { value: 'completed', label: 'Completed', disabled: false }
                        ]
                  }
                  />
                )}
              </div>
            </Card>

            {/* Sidebar - Cycle & Dates */}
            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Timeline</h3>

                {/* Cycle Selector */}
                <div className="space-y-4">
                  <Select
                    label="Cycle"
                    name="cycleId"
                    value={formData.cycleId}
                    onChange={handleCycleChange}
                    options={[
                      { value: '', label: 'No Cycle', disabled: false },
                      ...cycles.map(cycle => ({
                        value: cycle.id,
                        label: `${cycle.name}${cycle.status === 'active' ? ' (Active)' : ''}`,
                        disabled: false
                      }))
                    ]}
                    disabled={loadingCycles}
                  />
                  {formData.cycleId && datesFromCycle && (
                    <p className="text-xs text-primary-600 bg-primary-50 px-3 py-2 rounded-lg">
                      Dates auto-filled from cycle. You can modify them below.
                    </p>
                  )}

                  {/* Date Range */}
                  <Input
                    label="Start Date"
                    name="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={handleChange}
                  />
                  <Input
                    label="End Date"
                    name="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={handleChange}
                  />
                </div>
              </Card>

              {/* Quick Tips */}
              <Card className="p-6 bg-sand border-stone-200">
                <h3 className="text-sm font-semibold text-stone-700 mb-3">Tips for Great OKRs</h3>
                <ul className="text-sm text-stone-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-primary-500 mt-0.5">•</span>
                    <span>Make objectives aspirational but achievable</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary-500 mt-0.5">•</span>
                    <span>Key results should be measurable and time-bound</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary-500 mt-0.5">•</span>
                    <span>Aim for 3-5 key results per objective</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>

          {/* Key Results Section - Full width */}
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Key Results</h2>
                <p className="text-sm text-stone-500 mt-1">Define measurable outcomes that indicate success</p>
              </div>
              {!isEditMode && (
                <Button type="button" variant="secondary" onClick={addKeyResult}>
                  + Add Key Result
                </Button>
              )}
            </div>

            {isEditMode && (
              <div className="mb-4 bg-primary-50 border border-primary-200 text-primary-700 px-4 py-3 rounded-lg text-sm">
                Note: Key Results cannot be edited here. Please use the OKR details page to manage Key Results.
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {keyResults.map((kr, index) => (
                <div key={index} className="border border-stone-200 rounded-xl p-5 bg-sand hover:shadow-soft-sm transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </span>
                      <h3 className="font-medium text-stone-900">Key Result</h3>
                    </div>
                    {!isEditMode && keyResults.length > 1 && (
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => removeKeyResult(index)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <Input
                      label="Title"
                      value={kr.title}
                      onChange={(e) => handleKeyResultChange(index, 'title', e.target.value)}
                      placeholder="e.g., Achieve NPS score of 50"
                      disabled={isEditMode}
                    />

                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        label="Start"
                        type="number"
                        value={kr.startValue}
                        onChange={(e) => handleKeyResultChange(index, 'startValue', e.target.value)}
                        disabled={isEditMode}
                      />
                      <Input
                        label="Target"
                        type="number"
                        value={kr.targetValue}
                        onChange={(e) => handleKeyResultChange(index, 'targetValue', e.target.value)}
                        disabled={isEditMode}
                      />
                      <Input
                        label="Unit"
                        value={kr.unit}
                        onChange={(e) => handleKeyResultChange(index, 'unit', e.target.value)}
                        placeholder="%"
                        disabled={isEditMode}
                      />
                    </div>

                    {/* KR Owner - only for team-type OKRs with a selected team */}
                    {formData.type === 'team' && formData.teamId && teamMembers.length > 0 && !isEditMode && (
                      <Select
                        label="Assign to"
                        value={kr.ownerId || ''}
                        onChange={(e) => handleKeyResultChange(index, 'ownerId', e.target.value)}
                        options={[
                          { value: '', label: 'Unassigned', disabled: false },
                          ...teamMembers.map(m => ({
                            value: m.userId,
                            label: `${m.firstName} ${m.lastName} (${m.department})`,
                            disabled: false
                          }))
                        ]}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {errors.submit && (
            <div className="mb-6 bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg">
              {errors.submit}
            </div>
          )}

          {/* Action buttons - Sticky on mobile */}
          <div className="flex justify-end gap-3 sticky bottom-4 bg-cream py-4 -mx-6 px-6 lg:static lg:bg-transparent lg:py-0 lg:mx-0 lg:px-0">
            <Button type="button" variant="secondary" onClick={goBack}>
              Cancel
            </Button>
            {!isEditMode && (
              <Button
                type="button"
                variant="outline"
                onClick={handleCreateAndSubmit}
                disabled={isLoading}
              >
                {submitForApprovalMutation.isPending ? (
                  <span className="flex items-center">
                    <LoadingSpinner size="sm" className="mr-2" />
                    Submitting...
                  </span>
                ) : (
                  'Create & Submit for Approval'
                )}
              </Button>
            )}
            <Button type="submit" variant="primary" disabled={isLoading}>
              {mutation.isPending && !submitForApprovalMutation.isPending ? (
                <span className="flex items-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </span>
              ) : (
                isEditMode ? 'Update OKR' : 'Create OKR'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
